import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  ListItemButton,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  LocalShipping as ShippingIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  CardGiftcard as GiftIcon,
  AttachMoney as MoneyIcon,
  CameraAlt as CameraIcon,
  LocalShipping as TruckIcon,
  ShoppingCart as CartIcon,
  Chat as ChatIcon,
  EmojiEvents as TrophyIcon,
  GetApp as DownloadIcon,
  Favorite as HeartIcon,
  FlightTakeoff as RocketIcon,
  Language as LanguageIcon,
  Menu as MenuIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { images } from "../../assets/AssetHelper";
import zIndex from "@mui/material/styles/zIndex";

// Fixed Header Component
const FixedHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = useMemo(
    () => [
      { text: t("landing.menu.home"), href: "#home_wrapper" },
      { text: t("landing.menu.features"), href: "#features" },
      { text: t("landing.menu.gallery"), href: "#gallery" },
      { text: t("landing.menu.contact"), href: "#footer" },
    ],
    [t],
  );

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Box sx={{ my: 2 }}>
        <img src={images.Logo} alt="Cadeau Logo" style={{ height: "40px" }} />
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              sx={{ textAlign: "center" }}
              component="a"
              href={item.href}
            >
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}

        <ListItem disablePadding>
          <ListItemButton
            onClick={toggleLanguage}
            sx={(theme) => ({
              mx: 1.5,
              my: 1,
              borderRadius: "999px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              justifyContent: "center",
              gap: 1,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            })}
          >
            <ListItemIcon sx={{ minWidth: "auto", color: "inherit" }}>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: "center", m: 0 }}
              primary={i18n.language === "en" ? "العربية" : "English"}
              primaryTypographyProps={{ fontWeight: 700 }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        ...styles.fixedHeader,
        background:
          "linear-gradient(318deg, rgb(177 102 234) 0%, rgb(81 5 159) 100%)",
        backgroundColor: "rgba(0, 0, 0, 0.0)!important",
        boxShadow: isScrolled ? 1 : "none",
        transition: "all 0.3s ease",
        color: "white",
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Container maxWidth="lg" sx={{ maxWidth: "100vw" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            component="a"
            href="#home_wrapper"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <img
              src={images.landingLogo}
              alt="Cadeau Logo"
              style={{ height: "40px" }}
            />
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {menuItems.map((item) => (
                <a
                  key={item.text}
                  href={item.href}
                  style={{
                    fontSize: "14px !important",
                    lineHeight: "30px !important",
                    textTransform: "uppercase",
                    padding: "10px 20px !important",
                    fontWeight: 700,
                    fontFamily: "'Cabin', sans-serif",
                    padding: "10px 20px",
                    color: "white",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                >
                  {item.text}
                </a>
              ))}
              <Button
                color="inherit"
                onClick={toggleLanguage}
                sx={{
                  marginInlineStart: 2,
                  fontFamily: "'Cabin', sans-serif",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                }}
              >
                {i18n.language === "en" ? "العربية" : "English"}
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>

      <Drawer
        anchor={i18n.language === "ar" ? "left" : "right"}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: "block", md: "none" },
          zIndex: theme.zIndex.drawer + 1,
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

// Custom CSS
const styles = {
  heroSection: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    paddingTop: { xs: 8, md: 12 },
    paddingBottom: { xs: 8, md: 12 },
    textAlign: "center",
  },
  featureCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: 4,
    },
  },
  gallerySection: {
    backgroundColor: "#764ba2",
    color: "white",
    padding: { xs: 4, md: 8 },
  },
  statsSection: {
    backgroundColor: "#ffff",
    padding: { xs: 4, md: 8 },
  },
  statBox: {
    textAlign: "center",
    padding: 3,
  },
  statIcon: {
    fontSize: 40,
    color: "#FF6B35",
    marginBottom: 2,
  },
  ctaSection: {
    backgroundColor: "#f5f5f5",
    padding: { xs: 4, md: 8 },
    textAlign: "center",
  },
  footer: {
    position: "relative",
    color: "white",
    textAlign: "center",
    padding: "110px 0 55px 0",
    overflow: "hidden",
    "&::before": {
      background:
        "linear-gradient(135deg, #990fc3 0, #760fc3 16%, #652ec3 52%, #652ec3 52%, #0dd3c5 100%, #7db9e8 100%)",
      borderRadius: "750px",
      content: '""',
      height: "1250px",
      left: "-360px",
      position: "absolute",
      top: "40px",
      transform: "rotate(-184deg)",
      width: "2250px",
      zIndex: 0,
      pointerEvents: "none",
      zIndex: "-1",
    },
  },
  socialIcon: {
    color: "white",
    margin: 1,
    transition: "transform 0.2s",
    "&:hover": {
      transform: "scale(1.2)",
    },
  },
  fixedHeader: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    transition: "all 0.3s ease",
  },
};

// Embla Carousel Component
const EmblaCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    slidesToScroll: 1,
  });

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const slideWidth = isMdUp ? "20%" : isSmUp ? "33.3333%" : "100%";
  const maxHeight = isMdUp ? "500px" : isSmUp ? "500px" : "700px";

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollTo = useCallback(
    (index) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <>
      <div
        className="embla"
        ref={emblaRef}
        style={{ overflow: "hidden", width: "100%" }}
      >
        <div
          className="embla__container"
          style={{ display: "flex", margin: "0 -16px" }}
        >
          <div
            className="embla__slide"
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              position: "relative",
              padding: "16px",
            }}
          >
            <img
              src={images.landingShowcase1}
              alt="App Showcase 1"
              style={{
                width: "100%",
                height: "auto",
                maxHeight,
                borderRadius: 8,
              }}
            />
          </div>
          <div
            className="embla__slide"
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              position: "relative",
              padding: "16px",
            }}
          >
            <img
              src={images.landingShowcase2}
              alt="App Showcase 2"
              style={{
                width: "100%",
                height: "auto",
                maxHeight,
                borderRadius: 8,
              }}
            />
          </div>
          <div
            className="embla__slide"
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              position: "relative",
              padding: "16px",
            }}
          >
            <img
              src={images.landingShowcase3}
              alt="App Showcase 3"
              style={{
                width: "100%",
                height: "auto",
                maxHeight,
                borderRadius: 8,
              }}
            />
          </div>
          <div
            className="embla__slide"
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              position: "relative",
              padding: "16px",
            }}
          >
            <img
              src={images.landingShowcase4}
              alt="App Showcase 4"
              style={{
                width: "100%",
                height: "auto",
                maxHeight,
                borderRadius: 8,
              }}
            />
          </div>
          <div
            className="embla__slide"
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              position: "relative",
              padding: "16px",
            }}
          >
            <img
              src={images.landingShowcase5}
              alt="App Showcase 5"
              style={{
                width: "100%",
                height: "auto",
                maxHeight,
                borderRadius: 8,
              }}
            />
          </div>
          <div
            className="embla__slide"
            style={{
              flex: `0 0 ${slideWidth}`,
              minWidth: 0,
              position: "relative",
              padding: "16px",
            }}
          >
            <img
              src={images.landingShowcase6}
              alt="App Showcase 6"
              style={{
                width: "100%",
                height: "auto",
                maxHeight,
                borderRadius: 8,
              }}
            />
          </div>
        </div>
      </div>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {scrollSnaps.map((_, index) => (
            <Box
              key={index}
              onClick={() => scrollTo(index)}
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                mx: 0.5,
                backgroundColor: index === selectedIndex ? "#FF6B35" : "#ccc",
                cursor: "pointer",
              }}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

// App Store Button Component
const AppStoreButton = ({ onClick, variant = "contained" }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -5 }}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.35)",
        borderRadius: "14px",
        padding: "14px 22px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        minWidth: "220px",
        color: "white",
        backdropFilter: "blur(2px)",
        "&:hover": {
          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
          backgroundColor: "rgba(0,0,0,0.5)",
          borderColor: "rgba(255,255,255,0.6)",
        },
      }}
    >
      <Box
        sx={{
          width: "40px",
          height: "40px",
          backgroundColor: "white",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginInlineEnd: "14px",
        }}
      >
        <AppleIcon sx={{ fontSize: "24px", color: "#111" }} />
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            color: "white",
            display: "block",
            lineHeight: 1,
          }}
        >
          {t("landing.hero.download_on")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
            color: "white",
            lineHeight: 1,
            marginTop: "2px",
          }}
        >
          App Store
        </Typography>
      </Box>
    </Box>
  );
};

// Google Play Button Component
const GooglePlayButton = ({ onClick, variant = "contained" }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -5 }}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(0,0,0,0.35)",
        border: "1px solid rgba(255,255,255,0.35)",
        borderRadius: "14px",
        padding: "14px 22px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        minWidth: "220px",
        color: "white",
        backdropFilter: "blur(2px)",
        "&:hover": {
          boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
          backgroundColor: "rgba(0,0,0,0.5)",
          borderColor: "rgba(255,255,255,0.6)",
        },
      }}
    >
      <Box
        sx={{
          width: "40px",
          height: "40px",
          backgroundColor: "white",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginInlineEnd: "14px",
        }}
      >
        <AndroidIcon sx={{ fontSize: "24px", color: "#111" }} />
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: "10px",
            color: "white",
            display: "block",
            lineHeight: 1,
          }}
        >
          {t("landing.hero.get_it_on")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
            color: "white",
            lineHeight: 1,
            marginTop: "2px",
          }}
        >
          Google Play
        </Typography>
      </Box>
    </Box>
  );
};

// Hero Section Component
const HeroSection = ({ onGooglePlay, onAppStore }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{
        ...styles.heroSection,
        backgroundImage: `url(${images.landingBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        paddingTop: { xs: 16, md: 20 }, // Extra padding for fixed header
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          background:
            "linear-gradient(135deg, #990fc3 0, #760fc3 16%, #652ec3 52%, #652ec3 52%, #0dd3c5 100%, #7db9e8 100%)",
          borderRadius: "750px",
          height: "1250px",
          left: "-400px",
          top: "-605px",
          transform: "rotate(-174deg)",

          width: "2250px",
          zIndex: -100,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,

          zIndex: 1,
        },
      }}
      id="home_wrapper"
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              component={motion.h1}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              variant={isMobile ? "h3" : "h2"}
              gutterBottom
              sx={{ fontWeight: "bold", mb: 3, color: "white" }}
            >
              {t("landing.hero.title")}
            </Typography>
            <Typography
              component={motion.p}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              variant={isMobile ? "h6" : "h5"}
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: "600px",
                mx: "auto",
                color: "white",
              }}
            >
              {t("landing.hero.subtitle")}
            </Typography>
            <Stack
              component={motion.div}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent={{ xs: "center", md: "flex-start" }}
              sx={{ mt: 4 }}
            >
              <AppStoreButton
                onClick={() =>
                  window.open(
                    "https://apps.apple.com/eg/app/cadeau/id6752618099",
                    "_blank",
                  )
                }
                variant="contained"
              />
              <GooglePlayButton
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.inzox.cadeau",
                    "_blank",
                  )
                }
                variant="outlined"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <img
                src={images.landingIphones}
                alt="Cadeau App on iPhone"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  filter: "drop-shadow(0px 12px 28px rgba(0,0,0,0.5))",
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Features Section Component
// Features Section Component
const FeaturesSection = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const features = useMemo(
    () => [
      {
        icon: <GiftIcon sx={{ fontSize: 40, color: "white" }} />,
        title: t("landing.features.items.gifts.title"),
        description: t("landing.features.items.gifts.desc"),
      },
      {
        icon: <MoneyIcon sx={{ fontSize: 40, color: "white" }} />,
        title: t("landing.features.items.payments.title"),
        description: t("landing.features.items.payments.desc"),
      },
      {
        icon: <CameraIcon sx={{ fontSize: 40, color: "white" }} />,
        title: t("landing.features.items.vouchers.title"),
        description: t("landing.features.items.vouchers.desc"),
      },
      {
        icon: <TruckIcon sx={{ fontSize: 40, color: "white" }} />,
        title: t("landing.features.items.delivery.title"),
        description: t("landing.features.items.delivery.desc"),
      },
      {
        icon: <CartIcon sx={{ fontSize: 40, color: "white" }} />,
        title: t("landing.features.items.order.title"),
        description: t("landing.features.items.order.desc"),
      },
      {
        icon: <ChatIcon sx={{ fontSize: 40, color: "white" }} />,
        title: t("landing.features.items.support.title"),
        description: t("landing.features.items.support.desc"),
      },
    ],
    [t],
  );

  return (
    <Box
      component="section"
      id="features"
      sx={{ py: 8, backgroundColor: "#fff" }}
    >
      <Container maxWidth="lg">
        <Typography
          component={motion.h2}
          initial={{ y: -20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          variant="h4"
          textAlign="center"
          gutterBottom
          sx={{ fontWeight: "bold", mb: 2 }}
        >
          {t("landing.features.title")}
        </Typography>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={4}>
            {features.slice(0, 3).map((feature, index) => (
              <Box
                component={motion.div}
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                key={index}
                sx={{ mb: 4, display: "flex", flexDirection: "row-reverse" }}
              >
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg,#990fc3 0,#760fc3 16%,#652ec3 52%,#652ec3 52%,#0dd3c5 100%,#7db9e8 100%)",

                    borderRadius: "25px 25px 0 25px",
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {feature.icon}
                </Box>
                <Box sx={{ marginInlineEnd: 4, textAlign: "right" }}>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Box
              component={motion.div}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={images.landingFeaturesIphone}
                alt="iPhone with Cadeau App"
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            {features.slice(3, 6).map((feature, index) => (
              <Box
                component={motion.div}
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                key={index}
                sx={{ mb: 4, display: "flex" }}
              >
                <Box
                  sx={{
                    background:
                      "linear-gradient(135deg,#990fc3 0,#760fc3 16%,#652ec3 52%,#652ec3 52%,#0dd3c5 100%,#7db9e8 100%)",
                    backgroundColor: "white",
                    borderRadius: "25px 25px 25px 0",
                    width: 60,
                    height: 60,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginInlineEnd: 4,
                  }}
                >
                  {feature.icon}
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Gallery Section Component
const GallerySection = () => {
  const { t, i18n } = useTranslation();

  return (
    <Box
      component="section"
      id="gallery"
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        background:
          "-webkit-linear-gradient(135deg,#990fc3 0,#760fc3 16%,#652ec3 52%,#652ec3 52%,#0dd3c5 100%,#7db9e8 100%)",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          component={motion.h2}
          initial={{ y: -20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          variant="h4"
          textAlign="center"
          gutterBottom
          sx={{ fontWeight: "bold", mb: 4, color: "white" }}
        >
          {t("landing.gallery.title")}
        </Typography>
      </Container>
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        sx={{ mt: 4 }}
      >
        <Box sx={{ position: "relative", overflow: "hidden", width: "100%" }}>
          <EmblaCarousel />
        </Box>
      </Box>
    </Box>
  );
};

// Statistics Section Component
const StatisticsSection = () => {
  const { t, i18n } = useTranslation();

  const stats = useMemo(
    () => [
      {
        icon: <TrophyIcon sx={styles.statIcon} />,
        number: "5K+",
        title: t("landing.stats.trusted_by"),
        subtitle: t("landing.stats.app_of_year"),
      },
      {
        icon: <DownloadIcon sx={styles.statIcon} />,
        number: "11500",
        title: t("landing.stats.total_downloads"),
        subtitle: "",
      },
      {
        icon: <HeartIcon sx={styles.statIcon} />,
        number: "5500",
        title: t("landing.stats.active_members"),
        subtitle: "",
      },
      {
        icon: <RocketIcon sx={styles.statIcon} />,
        number: "1000+",
        title: t("landing.stats.avatars"),
        subtitle: "",
      },
    ],
    [t],
  );

  return (
    <Box component="section" sx={styles.statsSection}>
      <Container maxWidth="lg">
        <Typography
          component={motion.h2}
          initial={{ y: -20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          variant="h4"
          textAlign="center"
          gutterBottom
          sx={{ fontWeight: "bold", mb: 4 }}
        >
          {t("landing.stats.title")}
        </Typography>

        <Grid container spacing={4}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                component={motion.div}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                sx={styles.statBox}
              >
                {stat.icon}
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{ fontWeight: "bold", my: 1 }}
                >
                  {stat.number}
                </Typography>
                <Typography variant="h6">{stat.title}</Typography>
                {stat.subtitle && (
                  <Typography variant="body2" color="text.secondary">
                    {stat.subtitle}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box
          component={motion.div}
          initial={{ scale: 0.9, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          sx={{ mt: 8, textAlign: "center" }}
        >
          <img
            src={images.landingIphoneV}
            alt="iPhone with Cadeau App"
            style={{ maxWidth: "100%", height: "auto", maxHeight: "500px" }}
          />
        </Box>
      </Container>
    </Box>
  );
};

// Footer Section Component
const FooterSection = () => {
  return (
    <Box component="footer" id="footer" sx={styles.footer}>
      <Container maxWidth="lg">
        <Typography variant="body2" align="center">
          {`Copyright © ${new Date().getFullYear()} Cadeau. Designed by `}
          <Link
            href="https://inzox.com"
            target="_blank"
            rel="noopener"
            sx={{ color: "inherit", fontWeight: "bold" }}
          >
            INZOX LLC.
          </Link>
        </Typography>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <Link href="#" sx={styles.socialIcon} aria-label="Twitter">
            <TwitterIcon />
          </Link>
          <Link href="#" sx={styles.socialIcon} aria-label="LinkedIn">
            <LinkedInIcon />
          </Link>
          <Link href="#" sx={styles.socialIcon} aria-label="Facebook">
            <FacebookIcon />
          </Link>
          <Link href="#" sx={styles.socialIcon} aria-label="Instagram">
            <InstagramIcon />
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

const LandingPage = ({ isAuth = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleGooglePlay = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.inzox.cadeau",
      "_blank",
    );
  };

  const handleAppStore = () => {
    window.open("https://apps.apple.com/eg/app/cadeau/id6752618099", "_blank");
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <FixedHeader />
      <HeroSection
        onGooglePlay={handleGooglePlay}
        onAppStore={handleAppStore}
      />
      <FeaturesSection />
      <GallerySection id="gallery" />
      <StatisticsSection id="features" />
      <FooterSection />
    </Box>
  );
};

export default LandingPage;
